import {
  articleRegistry,
  authorHonorificSuffixes,
  BLOG_DESCRIPTION,
  BLOG_PATH,
  type ArticleDefinition,
} from "@/features/blog/articleRegistry";
/**
 * JSON-LD Structured Data for SEO and LLM Discoverability
 * Provides machine-readable information about Evangelist's services
 */

import { landingFaqItems } from "./landingFaqContent";
import { siteConfig } from "./metadata";

/**
 * Publisher/organization logo for the schemas below.
 *
 * Raster, not the site's `logo-vector.svg`: Google does not support SVG for logo properties, so
 * the previous value parsed but was ignored everywhere it appeared.
 *
 * `logo-mark-512.png` is a mechanical re-export of that same `logo-vector.svg` at 512x512 —
 * identical artwork, black on transparency, which is exactly how the site paints it (the mark is
 * loaded through an `<img>`, so its `var(--fill-0, black)` can never be set from outside, and
 * dark surfaces invert it with a CSS filter). Size matters here: Google wants at least 112px on
 * the shortest side, so the 242x48 wordmark would have been rejected for the same rich results
 * this is meant to restore. Square clears it with room to spare.
 */
const ORGANIZATION_LOGO = {
  "@type": "ImageObject",
  url: `${siteConfig.url}/assets/logo-mark-512.png`,
  width: 512,
  height: 512,
};

/**
 * Organization Schema
 * Helps search engines and LLMs understand who Evangelist is
 */
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: siteConfig.name,
  url: siteConfig.url,
  logo: ORGANIZATION_LOGO,
  description: siteConfig.description,
  email: siteConfig.contact.sales,
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    email: siteConfig.contact.sales,
  },
  // Ties the domain to the real-world company for entity resolution. Derived from `siteConfig`,
  // which is also where the `article:author` profile URL comes from — one list, no drift.
  sameAs: Object.values(siteConfig.socialProfiles),
};

/**
 * SoftwareApplication Schema
 * Describes Evangelist as an enterprise platform
 */
const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Evangelist",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: "Enterprise programmatic short-form video exchange",
  },
  description:
    "Enterprise-grade programmatic exchange for short-form video advertising. Connect media buyers with verified clipping networks across YouTube Shorts, TikTok, and Instagram Reels with built-in brand safety and zkTLS verification.",
  featureList: [
    "Programmatic short-form video buying",
    "Multi-platform distribution (YouTube Shorts, TikTok, Instagram Reels)",
    "zkTLS-verified creator data",
    "Multimodal AI brand safety screening",
    "Verified view tracking",
    "Enterprise-grade fraud prevention",
    "Transparent measurement and reporting",
    "Automated settlement and payments",
  ],
};

/**
 * Service Schema for Media Buyers
 */
const mediaBuyerServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Advertising and Marketing",
  name: "Short-Form Video Advertising for Brands and Agencies",
  description:
    "Buy verified short-form creator content across YouTube Shorts, TikTok, and Instagram Reels. Enterprise-grade platform with brand safety verification, transparent measurement, and fraud prevention.",
  provider: {
    "@type": "Organization",
    name: siteConfig.name,
    url: siteConfig.url,
  },
  areaServed: "Worldwide",
  audience: {
    "@type": "Audience",
    audienceType: "Enterprise media buyers, advertising agencies, and brands",
  },
  // No `price`: it is a numeric property, and the previous "Contact for pricing" string failed
  // validation, which voided the offer rather than communicating that pricing is on enquiry.
  // An Offer carrying availability and a contact URL says that correctly.
  offers: {
    "@type": "Offer",
    description: "Enterprise account for programmatic short-form video buying",
    availability: "https://schema.org/InStock",
    url: `${siteConfig.url}/for-agencies`,
    priceCurrency: "USD",
  },
};

/**
 * Service Schema for Clipping Platforms
 */
const clippingPlatformServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Content Distribution",
  name: "Creator Network Monetization for Clipping Platforms",
  description:
    "Monetize your clipping community by connecting to tier-1 brand campaigns. Access $100K+ order sizes with automated brand safety, compliance, and payments.",
  provider: {
    "@type": "Organization",
    name: siteConfig.name,
    url: siteConfig.url,
  },
  areaServed: "Worldwide",
  audience: {
    "@type": "Audience",
    audienceType: "Clipping platforms, Discord communities, and creator networks",
  },
};

/**
 * WebSite Schema with search action
 */
const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: siteConfig.name,
  url: siteConfig.url,
  description: siteConfig.description,
  publisher: {
    "@type": "Organization",
    name: siteConfig.name,
  },
};

/** Homepage answers share one source with the visible FAQ at every viewport. */
const landingFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: landingFaqItems.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: {
      "@type": "Answer",
      text: answer,
    },
  })),
};

function generateArticleSchema(article: ArticleDefinition) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    datePublished: article.publishedTime,
    dateModified: article.modifiedTime,
    // Article rich results want an image at least 1200px wide. The in-article diagrams are all
    // narrower than that, so this points at the 1200x630 share image until a dedicated one exists.
    image: [`${siteConfig.url}${siteConfig.ogImage}`],
    description: article.structuredDescription ?? article.description,
    url: `${siteConfig.url}${article.path}`,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteConfig.url}${article.path}`,
    },
    author: {
      "@type": "Person",
      name: article.author,
      honorificSuffix: authorHonorificSuffixes[article.author],
    },
    publisher: {
      "@type": "Organization",
      name: siteConfig.name,
      url: siteConfig.url,
      logo: ORGANIZATION_LOGO,
    },
    about: article.tags,
    keywords: article.tags.join(", "),
    articleSection: article.section,
    inLanguage: "en-US",
    // The article is not paywalled or gated. Stating it explicitly stops a crawler treating the
    // page as partial content it is only seeing a preview of.
    isAccessibleForFree: true,
    isPartOf: {
      "@type": "WebSite",
      name: siteConfig.name,
      url: siteConfig.url,
    },
  };
}

/**
 * BreadcrumbList Schema for navigation
 */
function generateBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${siteConfig.url}${item.url}`,
    })),
  };
}

/**
 * Get all structured data for a page
 */
type PolicyStructuredData = {
  name: string;
  url: string;
  description: string;
};

const POLICY_DESCRIPTIONS: Record<"privacy-policy" | "terms-of-service", PolicyStructuredData> = {
  "privacy-policy": {
    name: "Privacy Policy",
    url: "/privacy-policy",
    description:
      "Details Evangelist's data collection, usage, retention, and user privacy choices for the Evangelist Exchange platform.",
  },
  "terms-of-service": {
    name: "Campaign Participation Terms",
    url: "/terms-of-service",
    description:
      "Outlines campaign eligibility, acceptable use, commercial terms, and legal obligations for platform participants engaging with Evangelist Exchange.",
  },
};

function generatePolicyWebPageSchema(policy: PolicyStructuredData) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: policy.name,
    url: `${siteConfig.url}${policy.url}`,
    description: policy.description,
    isPartOf: {
      "@type": "WebSite",
      name: siteConfig.name,
      url: siteConfig.url,
    },
  };
}

export function getArticleStructuredData(article: ArticleDefinition) {
  return [
    organizationSchema,
    websiteSchema,
    softwareApplicationSchema,
    generateArticleSchema(article),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Blog", url: BLOG_PATH },
      { name: article.title, url: article.path },
    ]),
  ];
}

export function getStructuredData(
  page: "home" | "for-agencies" | "for-clipping-platforms" | "sign-in" | "privacy-policy" | "terms-of-service" | "blog",
) {
  const baseSchemas = [organizationSchema, websiteSchema, softwareApplicationSchema];

  switch (page) {
    case "home":
      return [...baseSchemas, landingFaqSchema];
    case "blog":
      return [
        ...baseSchemas,
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Blog",
          description: BLOG_DESCRIPTION,
          url: `${siteConfig.url}${BLOG_PATH}`,
          mainEntity: {
            "@type": "ItemList",
            itemListElement: articleRegistry.map((article, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: article.title,
              url: `${siteConfig.url}${article.path}`,
            })),
          },
        },
        generateBreadcrumbSchema([
          { name: "Home", url: "/" },
          { name: "Blog", url: BLOG_PATH },
        ]),
      ];
    case "for-agencies":
      return [
        ...baseSchemas,
        mediaBuyerServiceSchema,
        generateBreadcrumbSchema([
          { name: "Home", url: "/" },
          { name: "For Agencies & Brands", url: "/for-agencies" },
        ]),
      ];
    case "for-clipping-platforms":
      return [
        ...baseSchemas,
        clippingPlatformServiceSchema,
        generateBreadcrumbSchema([
          { name: "Home", url: "/" },
          { name: "For Clipping Platforms", url: "/for-clipping-platforms" },
        ]),
      ];
    case "sign-in":
      return [
        ...baseSchemas,
        generateBreadcrumbSchema([
          { name: "Home", url: "/" },
          { name: "Sign in", url: "/signin" },
        ]),
      ];
    case "privacy-policy": {
      const policy = POLICY_DESCRIPTIONS["privacy-policy"];
      return [
        ...baseSchemas,
        generatePolicyWebPageSchema(policy),
        generateBreadcrumbSchema([
          { name: "Home", url: "/" },
          { name: policy.name, url: policy.url },
        ]),
      ];
    }
    case "terms-of-service": {
      const policy = POLICY_DESCRIPTIONS["terms-of-service"];
      return [
        ...baseSchemas,
        generatePolicyWebPageSchema(policy),
        generateBreadcrumbSchema([
          { name: "Home", url: "/" },
          { name: policy.name, url: policy.url },
        ]),
      ];
    }
    default:
      return baseSchemas;
  }
}
